import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ParameterPaginationTable from 'src/components/table/parameter-pagination-table'
import DatalakeService from 'src/services/datalake-service'
import { formatDateDataGrid } from 'src/utils/format-date'

const fields = ['Ticker', 'DataReferencia', 'Abertura', 'Fechamento', 'Maximo', 'Minimo', 'Quantidade', 'Volume']
const prices = ['Abertura', 'Fechamento', 'Maximo', 'Minimo']
const emptyPagination = { count: 0, next: null, previous: null }
const columns = ['ID', ...fields, 'CriadoEm', 'AtualizadoEm'].map(field => ({
    field, headerName: field, flex: 1, minWidth: 150, disableColumnMenu: true, sortable: false,
    editable: fields.includes(field),
    disableFilter: ['ID', 'Quantidade', 'Volume', 'CriadoEm', 'AtualizadoEm'].includes(field),
    ...(prices.includes(field) ? { typeComponent: 'number' } : {}),
    ...(field === 'DataReferencia' ? { typeComponent: 'date' } : {}),
    ...(['CriadoEm', 'AtualizadoEm'].includes(field) ? { renderCell: formatDateDataGrid } : {}),
}))

const businessData = row => Object.fromEntries(fields.map(field => {
    const value = row[field] === '' || row[field] === undefined ? null : row[field]
    return [field, prices.includes(field) && value !== null ? String(value).replace(',', '.') : value]
}))

const ParameterDatalakeCotacoesAcoesB3Table = ({ endpoint, loading, callbackError }) => {
    const [service] = useState(() => new DatalakeService())
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState(emptyPagination)
    const [resetPaginationKey, setResetPaginationKey] = useState(0)
    const generation = useRef(0)
    const busy = useRef(false)
    const filter = useRef('')

    const readPage = useCallback(async (request, reset = false) => {
        const current = ++generation.current
        loading(true)
        setData([])
        try {
            const { body } = await request()
            if (current !== generation.current) return
            setData(body.results.map(row => ({ ...row, id: row.ID })))
            setPagination({ count: body.count, next: body.next, previous: body.previous })
            if (reset) setResetPaginationKey(key => key + 1)
        } catch (error) {
            if (current === generation.current) {
                setPagination(emptyPagination)
                setResetPaginationKey(key => key + 1)
                callbackError(error)
            }
        } finally {
            if (current === generation.current) loading(false)
        }
    }, [loading, callbackError])

    useEffect(() => {
        filter.current = ''
        readPage(() => service.loadPaginationEndpoint(endpoint, '', 25, 0), true)
        return () => {
            generation.current += 1
            loading(false)
        }
    }, [endpoint, service, readPage, loading])

    const mutate = async action => {
        if (busy.current) throw new Error('Uma alteração já está em andamento.')
        const current = generation.current
        const stillCurrent = () => current === generation.current
        busy.current = true
        loading(true)
        try {
            return await action(stillCurrent)
        } finally {
            busy.current = false
            if (stillCurrent()) loading(false)
        }
    }

    const createRow = () => mutate(async stillCurrent => {
        await service.createTableEndpoint(endpoint, businessData({}))
        if (stillCurrent()) {
            filter.current = ''
            await readPage(() => service.loadPaginationEndpoint(endpoint, '', 25, 0), true)
        }
    })

    const updateRow = row => mutate(async stillCurrent => {
        const { body } = await service.updateTableEndpoint(endpoint, row.ID, businessData(row))
        const saved = { ...body, id: body.ID }
        if (stillCurrent()) setData(rows => rows.map(item => item.ID === saved.ID ? saved : item))
        return saved
    })

    const deleteRow = row => mutate(async stillCurrent => {
        await service.deleteTableEndpoint(endpoint, row.ID)
        if (stillCurrent()) {
            await readPage(() => service.loadPaginationEndpoint(endpoint, filter.current, 25, 0), true)
        }
    })

    const onFilter = (filterList, limit, offset) => {
        filter.current = new URLSearchParams(filterList.map(({ field, value }) => [field, value])).toString()
        return readPage(() => service.loadPaginationEndpoint(endpoint, filter.current, limit, offset), true)
    }

    return <ParameterPaginationTable
        columns={columns} data={data} setData={setData} pagination={pagination}
        createRow={createRow} updateRow={updateRow} deleteRow={deleteRow}
        filterFunction={onFilter} onChangePage={url => readPage(() => service.paginate(url))}
        resetPaginationKey={resetPaginationKey} customFilter={true} awaitRowUpdate={true}
    />
}

ParameterDatalakeCotacoesAcoesB3Table.propTypes = {
    endpoint: PropTypes.string.isRequired,
    loading: PropTypes.func.isRequired,
    callbackError: PropTypes.func.isRequired,
}

export default ParameterDatalakeCotacoesAcoesB3Table
